import React from 'react';
import { ArrowRight, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1767884162073-b0e3741aad63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxzdGFkaXVtJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwMTMzNTgwfDA&ixlib=rb-4.1.0&q=85"
          alt="Lumentec Lighting Solutions"
          loading="eager"
          width="1920"
          height="1080"
          sizes="100vw"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950/90 via-blue-900/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-blue-950/90 via-transparent to-transparent"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-5 py-2.5 mb-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)] animate-fade-in transition-all duration-500 hover:bg-white/20 cursor-default">
            <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
            <Lightbulb className="text-yellow-300" size={18} />
            <span className="text-white/90 text-sm font-semibold tracking-wide uppercase">ISO 9001:2015 Certified Excellence</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white mb-6 leading-tight tracking-tight animate-slide-up">
            Your Lighting
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-blue-100 to-white mt-2 drop-shadow-lg">
              Solutions Partner
            </span>
          </h1>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-blue-50 mb-8 max-w-2xl animate-slide-up-delay font-light leading-relaxed">
            Turnkey Project Specialists in High Mast Lighting, Industrial Illumination & Comprehensive Outdoor Lighting Solutions
          </p>

          {/* Description */}
          <p className="text-lg text-blue-200/90 mb-12 max-w-2xl leading-relaxed animate-slide-up-delay-2 font-medium">
            From consultation to commissioning, we deliver customized lighting systems engineered for excellence. Trusted by leading industries across highways, airports, stadiums, and industrial zones.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-5 animate-slide-up-delay-3 pb-8">
            <Button
              size="lg"
              onClick={() => scrollToSection('products')}
              className="group bg-white text-blue-950 hover:bg-blue-50 px-8 py-7 text-lg font-bold rounded-xl shadow-[0_0_40px_-10px_rgba(255,255,255,0.5)] hover:shadow-[0_0_60px_-15px_rgba(255,255,255,0.7)] transition-all duration-500 hover:-translate-y-1"
            >
              Explore Products
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform duration-300" size={22} />
            </Button>
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              variant="outline"
              className="bg-transparent border-2 border-white/60 text-white hover:bg-white/10 hover:border-white px-8 py-7 text-lg font-bold rounded-xl backdrop-blur-sm transition-all duration-500 hover:-translate-y-1"
            >
              Request Quote
            </Button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 animate-fade-in-up border-t border-white/10 pt-10">
            {[
              { value: '800+', label: 'Projects Completed' },
              { value: '15+', label: 'Years Experience' },
              { value: '50+', label: 'Cities Served' },
              { value: '100%', label: 'Quality Guarantee' }
            ].map((stat, index) => (
              <div key={index} className="text-left group cursor-default">
                <div className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200 mb-2 drop-shadow-sm transition-transform duration-300 group-hover:scale-105 origin-left">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base font-semibold text-blue-200/80 tracking-wide uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};

export default Hero;
